import { useState } from "react";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost" ? "http://localhost:5000" : "https://api.gubcpc.com");

const panelClass = "home-card-strong p-6 sm:p-8 lg:p-10 mb-6 sm:mb-8";
const sectionTitleClass = "home-title mt-4 text-2xl font-bold sm:text-4xl text-[var(--text)]";
const bodyTextClass = "text-sm sm:text-base home-muted leading-relaxed";
const primaryButtonClass = "home-btn-primary px-6 py-2.5 text-sm font-semibold sm:px-8 sm:py-3 sm:text-base cursor-pointer";
const inputClass = "w-full rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] px-4 py-3 text-sm text-[var(--text)] outline-none transition-all duration-300 placeholder:text-[#94A3B8]/60 focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)]";

const errorToast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  didOpen: (el) => {
    el.addEventListener("mouseenter", Swal.stopTimer);
    el.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required.";
    if (!emailRegex.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.subject.trim()) next.subject = "Subject is required.";
    if (!form.message.trim()) next.message = "Message is required.";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      errorToast.fire({ icon: "error", title: "Please fix the errors below before submitting." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/student/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to send your message.");

      await Swal.fire({
        icon: "success",
        title: "Message sent!",
        text: data.message || "We'll get back to you soon.",
        confirmButtonColor: "#0f6c49",
      });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      errorToast.fire({ icon: "error", title: err.message || "Failed to send your message." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="home-shell min-h-screen pt-24 pb-12">
      <div className="home-container px-4 sm:px-6">
        <section className={`${panelClass} max-w-2xl mx-auto`}>
          <span className="section-kicker mb-3">GET IN TOUCH</span>
          <h1 className={sectionTitleClass}>Contact Us</h1>
          <p className={`mt-4 ${bodyTextClass}`}>
            Questions, feedback, or collaboration ideas? Send us a message and we'll respond as soon as we can.
          </p>

          <form className="mt-8 grid gap-5" onSubmit={handleSubmit}>
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="grid gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text)]">Name</span>
                <input
                  className={`${inputClass} ${errors.name ? "border-rose-500" : ""}`}
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  placeholder="Your name"
                />
                {errors.name ? <span className="text-xs font-semibold text-rose-500">{errors.name}</span> : null}
              </label>
              <label className="grid gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text)]">Email</span>
                <input
                  type="email"
                  className={`${inputClass} ${errors.email ? "border-rose-500" : ""}`}
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="you@example.com"
                />
                {errors.email ? <span className="text-xs font-semibold text-rose-500">{errors.email}</span> : null}
              </label>
            </div>

            <label className="grid gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text)]">Subject</span>
              <input
                className={`${inputClass} ${errors.subject ? "border-rose-500" : ""}`}
                value={form.subject}
                onChange={(e) => updateField("subject", e.target.value)}
                placeholder="What's this about?"
              />
              {errors.subject ? <span className="text-xs font-semibold text-rose-500">{errors.subject}</span> : null}
            </label>

            <label className="grid gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text)]">Message</span>
              <textarea
                className={`${inputClass} min-h-[140px] resize-y`}
                value={form.message}
                onChange={(e) => updateField("message", e.target.value)}
                placeholder="Write your message here..."
              />
              {errors.message ? <span className="text-xs font-semibold text-rose-500">{errors.message}</span> : null}
            </label>

            <div className="flex justify-end">
              <button type="submit" disabled={submitting} className={`${primaryButtonClass} disabled:opacity-60`}>
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}
